import {
    Box,
    Drawer,
    IconButton,
    Typography,
} from '@compass/core';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { DashboardCardConfig, CardId} from '../types';
import { DashboardTabConfig, TabId, TabColorKey } from '../types/DashboardTabConfig';
import { pillToggleGroupStyles } from '../styles/segmentedControlStyles';
import { DrawerCardsView } from './DrawerCardsView';
import { DrawerTabsView } from './DrawerTabsView';

type DrawerView = 'cards' | 'tabs';

interface CardConfigurationDrawerProps {
    open: boolean;
    onClose: () => void;
    tabs: DashboardTabConfig[];
    activeTabId: TabId;
    cards: DashboardCardConfig[];
    isAtCardLimit: boolean;
    onToggleCard: (cardId: CardId) => void;
    onAddTab: (label: string) => void;
    onRenameTab: (tabId: TabId, newLabel: string) => void;
    onDeleteTab: (tabId: TabId) => void;
    onSetTabColor: (tabId: TabId, color: TabColorKey) => void;
}

export const CardConfigurationDrawer = ({
    open,
    onClose,
    tabs,
    activeTabId,
    cards,
    isAtCardLimit,
    onToggleCard,
    onAddTab,
    onRenameTab,
    onDeleteTab,
    onSetTabColor,
}: CardConfigurationDrawerProps) => {
    const [activeView, setActiveView] = useState<DrawerView>('cards');

    // Reset view when drawer closes
    useEffect(() => {
        if (!open) setActiveView('cards');
    }, [open]);

    const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: DrawerView | null) => {
        if (newView) setActiveView(newView);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDrawer-paper': {
                    width: 400,
                    maxWidth: '90vw',
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box
                    sx={{
                        p: 3,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h5">Manage Dashboard</Typography>
                    <IconButton onClick={onClose} size="small" aria-label="Close drawer">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Cards | Tabs Toggle */}
                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                    <ToggleButtonGroup
                        value={activeView}
                        exclusive
                        onChange={handleViewChange}
                        sx={pillToggleGroupStyles}
                    >
                        <ToggleButton value="cards">Cards</ToggleButton>
                        <ToggleButton value="tabs">Tabs</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* View Content */}
                {activeView === 'cards' ? (
                    <DrawerCardsView
                        tabs={tabs}
                        activeTabId={activeTabId}
                        cards={cards}
                        isAtCardLimit={isAtCardLimit}
                        onToggleCard={onToggleCard}
                    />
                ) : (
                    <DrawerTabsView
                        tabs={tabs}
                        activeTabId={activeTabId}
                        onAddTab={onAddTab}
                        onRenameTab={onRenameTab}
                        onDeleteTab={onDeleteTab}
                        onSetTabColor={onSetTabColor}
                    />
                )}
            </Box>
        </Drawer>
    );
};